import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import {
  Edit,
  Trash2,
  FileText,
  BookOpen,
  Calendar,
  MessageSquare,
  Heart,
  Eye,
  CheckCircle
} from 'lucide-react'

interface CategoryDetail {
  id: number
  slug: string
  name: string
  description: string | null
  position: number
  community_id: number | null
  community_name: string | null
  created_at: string
  updated_at: string
}

interface RecentPost {
  id: number
  title: string
  post_type: string
  author_name: string
  likes_count: number
  comments_count: number
  created_at: string
}

interface RecentArticle {
  id: number
  title: string
  slug: string
  author_name: string
  views_count: number
  published: boolean
  created_at: string
}

interface Stats {
  posts_count: number
  articles_count: number
}

interface ShowProps {
  category: CategoryDetail
  recent_posts: RecentPost[]
  recent_articles: RecentArticle[]
  stats: Stats
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short'
  })
}

function getPostTypeBadge(postType: string): { label: string; className: string } {
  switch (postType) {
    case 'question':
      return { label: 'Question', className: 'bg-blue-100 text-blue-800' }
    case 'discussion':
      return { label: 'Discussion', className: 'bg-green-100 text-green-800' }
    case 'announcement':
      return { label: 'Annonce', className: 'bg-yellow-100 text-yellow-800' }
    default:
      return { label: postType, className: 'bg-gray-100 text-gray-800' }
  }
}

export default function Show({ category, recent_posts, recent_articles, stats }: ShowProps) {
  const canDelete = stats.posts_count === 0 && stats.articles_count === 0

  const handleDelete = () => {
    if (!canDelete) {
      alert('Impossible de supprimer cette catégorie car elle contient des publications ou articles.')
      return
    }
    if (confirm(`Voulez-vous vraiment supprimer la catégorie "${category.name}" ?`)) {
      router.delete(`/admin/categories/${category.slug}`)
    }
  }

  return (
    <AdminLayout
      title={category.name}
      breadcrumbs={[
        { label: 'Catégories', href: '/admin/categories' },
        { label: category.name }
      ]}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Category Details */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-tessan-cream rounded-xl flex items-center justify-center">
                  <FileText size={28} className="text-tessan-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-text-dark">{category.name}</h2>
                  <p className="text-sm text-text-gray">/{category.slug}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Link href={`/admin/categories/${category.slug}/edit`}>
                  <Button variant="secondary" className="flex items-center gap-2">
                    <Edit size={16} />
                    Modifier
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  className={`flex items-center gap-2 ${
                    canDelete ? 'text-red-600 hover:bg-red-50' : 'text-gray-400 cursor-not-allowed'
                  }`}
                  onClick={handleDelete}
                  disabled={!canDelete}
                  title={!canDelete ? 'Contient des publications' : 'Supprimer'}
                >
                  <Trash2 size={16} />
                  Supprimer
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-text-gray">Description</p>
                <p className="text-text-dark">
                  {category.description || <span className="text-text-gray italic">Non définie</span>}
                </p>
              </div>

              <div>
                <p className="text-sm text-text-gray">Position</p>
                <p className="text-text-dark font-medium">#{category.position}</p>
              </div>

              <div>
                <p className="text-sm text-text-gray">Communauté</p>
                <p className="text-text-dark">
                  {category.community_name || <span className="text-text-gray italic">Non assignée</span>}
                </p>
              </div>

              <div>
                <p className="text-sm text-text-gray">Créée le</p>
                <p className="text-text-dark">{formatDate(category.created_at)}</p>
              </div>
            </div>
          </Card>

          {/* Recent Posts */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-dark flex items-center gap-2">
                <MessageSquare size={20} />
                Publications récentes
              </h3>
              <span className="text-sm text-text-gray">{stats.posts_count} total</span>
            </div>

            {recent_posts.length === 0 ? (
              <p className="text-text-gray text-center py-8">Aucune publication dans cette catégorie</p>
            ) : (
              <div className="space-y-3">
                {recent_posts.map((post) => {
                  const typeBadge = getPostTypeBadge(post.post_type)
                  return (
                    <div
                      key={post.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${typeBadge.className}`}>
                            {typeBadge.label}
                          </span>
                          <p className="font-medium text-text-dark truncate">{post.title}</p>
                        </div>
                        <p className="text-sm text-text-gray">
                          par {post.author_name} - {formatShortDate(post.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-text-gray ml-4">
                        <span className="flex items-center gap-1">
                          <Heart size={14} />
                          {post.likes_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare size={14} />
                          {post.comments_count}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>

          {/* Recent Articles */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-dark flex items-center gap-2">
                <BookOpen size={20} />
                Articles récents
              </h3>
              <span className="text-sm text-text-gray">{stats.articles_count} total</span>
            </div>

            {recent_articles.length === 0 ? (
              <p className="text-text-gray text-center py-8">Aucun article dans cette catégorie</p>
            ) : (
              <div className="space-y-3">
                {recent_articles.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {article.published ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 flex items-center gap-1">
                            <CheckCircle size={12} />
                            Publié
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                            Brouillon
                          </span>
                        )}
                        <p className="font-medium text-text-dark truncate">{article.title}</p>
                      </div>
                      <p className="text-sm text-text-gray">
                        par {article.author_name} - {formatShortDate(article.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-gray ml-4">
                      <Eye size={14} />
                      {article.views_count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Statistiques</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <MessageSquare size={20} className="text-blue-600" />
                  <span className="text-text-dark">Publications</span>
                </div>
                <span className="text-xl font-bold text-blue-600">{stats.posts_count}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <BookOpen size={20} className="text-green-600" />
                  <span className="text-text-dark">Articles</span>
                </div>
                <span className="text-xl font-bold text-green-600">{stats.articles_count}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText size={20} className="text-gray-600" />
                  <span className="text-text-dark">Total contenus</span>
                </div>
                <span className="text-xl font-bold text-text-dark">
                  {stats.posts_count + stats.articles_count}
                </span>
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Informations</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-text-gray">
                <Calendar size={16} />
                <span>Créée le {formatDate(category.created_at)}</span>
              </div>
              <div className="flex items-center gap-2 text-text-gray">
                <Calendar size={16} />
                <span>Modifiée le {formatDate(category.updated_at)}</span>
              </div>
            </div>
          </Card>

          {/* Actions Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Actions</h3>
            <div className="space-y-2">
              <Link href={`/admin/categories/${category.slug}/edit`} className="block">
                <Button variant="secondary" className="w-full justify-start gap-2">
                  <Edit size={16} />
                  Modifier la catégorie
                </Button>
              </Link>
              <Link href="/admin/categories" className="block">
                <Button variant="secondary" className="w-full justify-start gap-2">
                  <FileText size={16} />
                  Retour à la liste
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
